import { useState } from 'react';
import { Box, Button, FormHelperText, Typography } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useHasPermission } from '@/components/PermissionGate';
import { CommandResultSnackbar, type CommandResult } from '@/components/CommandResultSnackbar';
import { USER_PREF_KEYS } from '@/services/userPreferencesService';
import { ACCENT_COLORS, CUSTOM_ACCENT, DEFAULT_CUSTOM_THEME, type AccentColorKey } from '@/theme';
import { CustomThemeDialog } from './CustomThemeDialog';

const MANAGE_THEME_PERMISSION = 'Core.Theme.Manage';

interface SwatchProps {
  color: string;
  title: string;
  selected: boolean;
  onClick: () => void;
  /** A thin frame even when not chosen — what sets the tenant's own colours apart from the built-in ones. */
  framed?: boolean;
}

function swatchBorder(selected: boolean, framed: boolean): string {
  if (selected) return '3px solid';
  return framed ? '1px solid' : '2px solid transparent';
}

function Swatch({ color, title, selected, onClick, framed = false }: SwatchProps) {
  return (
    <Box
      onClick={onClick}
      title={title}
      sx={{
        width: 40,
        height: 40,
        borderRadius: 1,
        bgcolor: color,
        cursor: 'pointer',
        border: swatchBorder(selected, framed),
        borderColor: selected || framed ? 'text.primary' : 'transparent',
        transition: 'border-color 0.15s, transform 0.15s',
        '&:hover': { transform: 'scale(1.1)' },
      }}
    />
  );
}

/**
 * The accent colour choice: the built-in pairs, plus the tenant's own colours ("custom") once it
 * has them. Every user may choose them; a holder of Core.Theme.Manage also creates and edits them.
 */
export function AccentColorPicker() {
  const { t } = useTranslation();
  const { accentColor, customTheme, updatePreference } = useUserPreferences();
  const canManage = useHasPermission(MANAGE_THEME_PERMISSION);
  const [editorOpen, setEditorOpen] = useState(false);
  const [outcome, setOutcome] = useState<CommandResult | null>(null);

  const handleSaved = () => {
    setEditorOpen(false);
    setOutcome({ severity: 'success', message: t('settings.customTheme.saved') });
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('settings.accentColor')}</Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {(Object.entries(ACCENT_COLORS) as [AccentColorKey, { main: string; label: string }][]).map(
          ([key, { main, label }]) => (
            <Swatch
              key={key}
              color={main}
              title={label}
              selected={accentColor === key}
              onClick={() => updatePreference(USER_PREF_KEYS.accentColor, key)}
            />
          ),
        )}
        {customTheme && (
          <Swatch
            color={customTheme.light.primary}
            title={t('settings.customTheme.swatchTitle')}
            selected={accentColor === CUSTOM_ACCENT}
            onClick={() => updatePreference(USER_PREF_KEYS.accentColor, CUSTOM_ACCENT)}
            framed
          />
        )}
        {canManage && (
          <Button size="small" startIcon={<Edit />} onClick={() => setEditorOpen(true)}>
            {t(customTheme ? 'settings.customTheme.edit' : 'settings.customTheme.create')}
          </Button>
        )}
      </Box>
      <FormHelperText>{t('settings.accentColorHelp')}</FormHelperText>
      {editorOpen && (
        <CustomThemeDialog
          initial={customTheme ?? DEFAULT_CUSTOM_THEME}
          onClose={() => setEditorOpen(false)}
          onSaved={handleSaved}
        />
      )}
      <CommandResultSnackbar result={outcome} onClose={() => setOutcome(null)} />
    </Box>
  );
}
